import { resolveVariableColor, toast } from ".";

type DrawStyle = "line" | "bars";

const AUDIO_BUFFER_LENGTH = 2048;

export class RecordingRenderer {
    dataArray: Uint8Array<ArrayBuffer>;
    audioCtx: AudioContext;
    source?: MediaStreamAudioSourceNode;
    analyser?: AnalyserNode;

    constructor(stream: MediaStream) {
        this.audioCtx = new AudioContext();
        // Cast to match Web Audio API expectation - at runtime Uint8Array always uses ArrayBuffer
        this.dataArray = new Uint8Array(AUDIO_BUFFER_LENGTH) as Uint8Array<ArrayBuffer>;

        this.source = this.audioCtx.createMediaStreamSource(stream);
        this.analyser = this.audioCtx.createAnalyser();

        this.analyser.fftSize = AUDIO_BUFFER_LENGTH;
        this.source.connect(this.analyser);
    }

    draw(canvas: HTMLCanvasElement) {
        if (!this.analyser) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        this.analyser.getByteTimeDomainData(this.dataArray);

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.lineWidth = 2;
        ctx.strokeStyle = resolveVariableColor('--st-link-fg');
        ctx.beginPath();

        const sliceWidth = canvas.width / AUDIO_BUFFER_LENGTH;
        let x = 0;

        for (let i = 0; i < AUDIO_BUFFER_LENGTH; i++) {
            const v = this.dataArray[i] / 128.0; // normalize 0�1
            const y = (v * canvas.height) / 2;

            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);

            x += sliceWidth;
        }

        ctx.lineTo(canvas.width, canvas.height / 2);
        ctx.stroke();
    }
}

export interface WaveformOptions {
    style?: DrawStyle;
    color?: string;
    playheadColor?: string;
    background?: string;
    paddingPx?: number;
    barWidthPx?: number;
    barGapPx?: number;
    dpiAware?: boolean;
}

/**
 * Render a waveform to a <canvas> from mono Float32 samples (-1..1).
 * For interleaved/stereo data, pass just one channel (e.g., left).
 */
export function renderWaveform(canvas: HTMLCanvasElement, samples: Float32Array, opts: WaveformOptions = {}) {
    const {
        style = "line",
        color = resolveVariableColor('--st-link-fg'),
        playheadColor = resolveVariableColor('--st-page-fg'),
        background = "transparent",
        paddingPx = 2,
        barWidthPx = 2,
        barGapPx = 1,
        dpiAware = true,
    } = opts;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = dpiAware ? (window.devicePixelRatio || 1) : 1;
    const cssW = canvas.clientWidth || canvas.width;
    const cssH = canvas.clientHeight || canvas.height;
    canvas.width = Math.max(1, Math.floor(cssW * dpr));
    canvas.height = Math.max(1, Math.floor(cssH * dpr));
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    ctx.fillStyle = background;
    ctx.fillRect(0, 0, cssW, cssH);

    const innerW = cssW - paddingPx * 2;
    const innerH = cssH - paddingPx * 2;
    const midY = paddingPx + innerH / 2;

    const peaks = buildPeaks(samples, style === "bars" ? Math.floor(innerW / (barWidthPx + barGapPx)) : innerW);

    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = 1;

    if (style === "line") {
        ctx.beginPath();

        const step = innerW / samples.length;
        let x = paddingPx;

        ctx.moveTo(x, paddingPx + innerH / 2);
        for (const s of samples) {
            const y = midY - (s * innerH) / 2;
            ctx.lineTo(x, y);

            x += step;
        }
        ctx.lineTo(x, midY);
        ctx.lineTo(paddingPx + innerW, midY);

        ctx.stroke();
    } else {
        let x = paddingPx;
        for (let i = 0; i < peaks.length; i++) {
            const { max, min } = peaks[i];
            const top = midY - (max * innerH) / 2;
            const bottom = midY - (min * innerH) / 2;
            const h = bottom - top;
            ctx.fillRect(x, top, barWidthPx, h);
            x += barWidthPx + barGapPx;
            if (x > paddingPx + innerW) break;
        }
    }

    // Return a helper to draw/update a playhead at a given 0..1 progress
    return (progress01: number) => {
        const x = paddingPx + Math.max(0, Math.min(1, progress01)) * innerW;
        ctx.save();
        ctx.strokeStyle = playheadColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, paddingPx);
        ctx.lineTo(x, paddingPx + innerH);
        ctx.stroke();
        ctx.restore();
    };
}

/**
 * Attach a playhead synced to an <audio> element�s timeupdate.
 * Call after initial renderWaveform() so the canvas already has the waveform.
 */
export function attachPlayhead(canvas: HTMLCanvasElement, audio: HTMLAudioElement, drawPlayhead: (progress01: number) => void) {
    const redraw = () => {
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        // Quick clear + re-render background only; your app should store peaks and redraw waveform if needed.
        // Simpler: keep a pre-rendered waveform on an offscreen canvas and blit each frame.
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
    const onFrame = () => {
        const p = audio.duration ? audio.currentTime / audio.duration : 0;
        // For performance, you might want to keep the waveform cached; here we only draw the playhead.
        drawPlayhead(p);
        if (!audio.paused && !audio.ended) requestAnimationFrame(onFrame);
    };
    audio.addEventListener("play", () => requestAnimationFrame(onFrame));
    audio.addEventListener("timeupdate", onFrame);
}

/**
 * Builds per-pixel (or per-bar) min/max peaks using decimation with RMS guard.
 */
function buildPeaks(samples: Float32Array, targetBins: number): { min: number; max: number }[] {
    const len = samples.length;
    const bins = Math.max(1, Math.floor(targetBins));
    const block = len / bins;
    const out: { min: number; max: number }[] = new Array(bins);

    for (let i = 0; i < bins; i++) {
        const start = Math.floor(i * block);
        const end = Math.floor((i + 1) * block);
        let min = 1, max = -1, sumSq = 0, n = 0;
        for (let j = start; j < end && j < len; j++) {
            const v = Math.max(-1, Math.min(1, samples[j]));
            if (v < min) min = v;
            if (v > max) max = v;
            sumSq += v * v;
            n++;
        }
        // keep tiny energy visible
        const rms = Math.sqrt(sumSq / Math.max(1, n));
        const epsilon = 0.01 * rms;
        if (max - min < epsilon) {
            max = Math.min(1, rms + epsilon);
            min = Math.max(-1, -rms - epsilon);
        }
        out[i] = { min, max };
    }
    return out;
}

let mediaRecorder: MediaRecorder;
let recordedChunks: BlobPart[] = [];
let stream: MediaStream | null = null;

// Start recording
export async function startRecording() {
    try {
        await requestMic();
        if (!stream) return;

        mediaRecorder = new MediaRecorder(stream);

        recordedChunks = [];
        mediaRecorder.ondataavailable = event => {
            if (event.data.size > 0) {
                recordedChunks.push(event.data);
            }
        };

        mediaRecorder.start();

        return new RecordingRenderer(stream);
    }
    catch (err: any) {
        if (err.name === "NotFoundError") {
            toast.notify('error', 'No microphone found or enabled.');
        }
        else if (err.name === "NotAllowedError") {
            toast.notify('error', 'Permission denied for microphone.');
        }
        else {
            toast.notify('error', `Error starting recording: ${err}`);
            console.error("Error starting recording:", err);
        }

        throw err;
    }
}

// Stop recording and get a Blob
export function stopRecording(): Blob {
    return new Promise<Blob>(resolve => {
        mediaRecorder.onstop = () => {
            const audioBlob = new Blob(recordedChunks, { type: mediaRecorder.mimeType });

            // release the mic
            if (stream) {
                stream.getTracks().forEach(t => t.stop());
                stream = null;
            }

            resolve(audioBlob);
        };
        mediaRecorder.stop();
    }) as unknown as Blob;
}

type MicState = 'granted' | 'denied' | 'prompt' | 'unknown';
export async function canRecord(): Promise<MicState> {
    // Try Permissions API (Chrome/Edge/Firefox)
    if ('permissions' in navigator) {
        try {
            const result = await (navigator as any).permissions.query({ name: 'microphone' });
            return result.state as MicState;
        } catch {
            /* fall through */
        }
    }

    // Fallback: enumerateDevices (no prompt)
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        // If labels are visible, permission was already granted
        if (audioInputs.some(d => d.label)) return 'granted';
        return audioInputs.length > 0 ? 'unknown' : 'denied';
    } catch {
        return 'unknown';
    }
}


async function requestMic() {
    try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        return true;
    } catch (err: any) {
        switch (err?.name) {
            case "NotAllowedError":
                console.warn("Mic permission denied by user or policy.");
                break;
            case "NotFoundError":
                console.warn("No audio input device found.");
                break;
            case "NotReadableError":
                console.warn("Mic is already in use by another app.");
                break;
            case "OverconstrainedError":
                console.warn("Requested constraints don't match any device.");
                break;
            case "SecurityError":
                console.warn("Blocked by security policy (check iframe/headers).");
                break;
            default:
                console.warn("getUserMedia failed:", err);
        }
        return false;
    }
}
